import { useId, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { cn } from './cn'
import { useFieldControl } from './field'

export interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  /** Existing tags to suggest (filtered as you type). */
  suggestions?: string[]
  placeholder?: string
  /** Max number of tags. */
  max?: number
  /** Max characters per tag (default 40). */
  maxLength?: number
  /** Return an error message to reject a tag. */
  validate?: (tag: string) => string | null
  /** Case-insensitive de-duplication (default true). */
  caseInsensitive?: boolean
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
}

/**
 * Free-form tags: Enter, comma or Tab adds; Backspace on empty removes the last; pasting
 * "a, b, c" adds three. `<TagInput value={tags} onChange={setTags} suggestions={allTags} />`
 */
export function TagInput({ value, onChange, suggestions = [], placeholder = 'Add tag', max, maxLength = 40, validate, caseInsensitive = true, disabled, invalid, id, className }: TagInputProps) {
  const f = useFieldControl()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const [focused, setFocused] = useState(false)
  const [navigated, setNavigated] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const norm = (s: string) => (caseInsensitive ? s.toLowerCase() : s)
  const has = (t: string) => value.some(v => norm(v) === norm(t))

  const matches = useMemo(() => {
    const q = norm(text.trim())
    return suggestions.filter(s => !has(s) && (!q || norm(s).includes(q))).slice(0, 8)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, text, value])

  const add = (raw: string[]) => {
    const next = [...value]
    for (const r of raw) {
      const t = r.trim().replace(/\s+/g, ' ').slice(0, maxLength)
      if (!t) continue
      if (next.some(v => norm(v) === norm(t))) continue
      if (max !== undefined && next.length >= max) { setError(`Up to ${max} tags`); break }
      const err = validate?.(t)
      if (err) { setError(err); continue }
      next.push(t)
    }
    if (next.length !== value.length) { onChange(next); setError(null) }
    setText(''); setActive(0)
  }
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const open = focused && matches.length > 0
    const pick = open && navigated
    if (e.key === 'ArrowDown' && open) { e.preventDefault(); setNavigated(true); setActive(a => (navigated ? (a + 1) % matches.length : 0)) }
    else if (e.key === 'ArrowUp' && open) { e.preventDefault(); setNavigated(true); setActive(a => (a - 1 + matches.length) % matches.length) }
    else if (e.key === 'Enter' || e.key === ',' || (e.key === 'Tab' && (text.trim() || pick))) {
      if (e.key === 'Enter' && !text.trim() && !pick) return
      e.preventDefault()
      add([pick && e.key !== ',' ? matches[active] : text])
      setNavigated(false)
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1))
    } else if (e.key === 'Escape') { setText(''); setError(null) }
  }
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const t = e.clipboardData.getData('text')
    if (/[,\n\t]/.test(t)) { e.preventDefault(); add(t.split(/[,\n\t]/)) }
  }
  const isInvalid = invalid ?? f?.invalid ?? !!error
  return (
    <div className="relative flex flex-col gap-1">
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'flex min-h-8 w-full cursor-text flex-wrap items-center gap-1 rounded-control border border-input bg-surface px-1 py-1 shadow-control',
          'transition-[border-color,box-shadow] duration-150 hover:border-border-strong focus-within:border-focus focus-within:ring-3 focus-within:ring-focus/20',
          isInvalid && 'border-danger', disabled && 'cursor-not-allowed bg-surface-sunken opacity-70', className,
        )}
      >
        {value.map(t => (
          <span key={t} className="inline-flex h-6 max-w-full items-center gap-1 rounded-[6px] bg-surface-active pr-1 pl-2 text-sm text-text">
            <span className="truncate">{t}</span>
            {!disabled && (
              <button type="button" aria-label={`Remove ${t}`} onClick={e => { e.stopPropagation(); onChange(value.filter(v => v !== t)) }}
                className="grid size-4 place-items-center rounded text-text-subtle hover:bg-border hover:text-text focus-visible:outline-2 focus-visible:outline-focus"><X className="size-3" /></button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id ?? f?.id}
          value={text}
          disabled={disabled}
          onChange={e => { setText(e.target.value); setActive(0); setNavigated(false); setError(null) }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); if (text.trim()) add([text]) }}
          placeholder={value.length ? '' : placeholder}
          aria-invalid={isInvalid || undefined}
          aria-describedby={f?.describedBy}
          aria-autocomplete="list"
          aria-controls={matches.length ? listId : undefined}
          aria-activedescendant={focused && matches.length && navigated ? `${listId}-${active}` : undefined}
          role="combobox"
          aria-expanded={focused && matches.length > 0}
          className="h-6 min-w-24 flex-1 bg-transparent px-1 text-base text-text outline-none placeholder:text-text-subtle"
        />
      </div>
      {focused && matches.length > 0 && (
        <ul id={listId} role="listbox" className="absolute top-full right-0 left-0 z-30 mt-1 max-h-56 overflow-y-auto rounded-card border border-border bg-surface-raised p-1 shadow-popover animate-pop-in">
          {matches.map((m, i) => (
            <li key={m} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              onPointerDown={e => { e.preventDefault(); add([m]) }}
              onPointerEnter={() => { setActive(i); setNavigated(true) }}
              className={cn('flex h-8 cursor-default items-center rounded-[6px] px-2 text-base', navigated && i === active && 'bg-surface-active')}>
              {m}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-danger-strong" role="alert">{error}</p>}
    </div>
  )
}
